/* ================================================================
   Beer Republic — Header Scroll Behaviour
   Custom elements: <sticky-header>, <header-search>, <cart-counter>
   ================================================================ */

/* ── <sticky-header> ── */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.lastScrollY = 0;
      this.ticking = false;
      this.threshold = parseInt(this.dataset.scrollThreshold, 10) || 10;
      this.stickyType = this.dataset.stickyType || 'always';
      this.hidden = false;
    }

    connectedCallback() {
      if (this.stickyType === 'none') return;
      this.onScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.handleScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const pastThreshold = scrollY > this.threshold;

        this.dataset.scrolled = pastThreshold ? 'true' : 'false';

        if (this.stickyType === 'on-scroll-up') {
          if (scrollY > this.lastScrollY && pastThreshold) {
            this.style.transform = 'translateY(-100%)';
            this.hidden = true;
          } else if (scrollY < this.lastScrollY || !pastThreshold) {
            this.style.transform = 'translateY(0)';
            this.hidden = false;
          }
        }

        this.lastScrollY = scrollY;
        this.ticking = false;
      });
    }
  }

  customElements.define('sticky-header', StickyHeader);
}

/* ── <header-search> ── */
if (!customElements.get('header-search')) {
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-search-toggle]');
      this.overlay = document.getElementById('SearchOverlay');
      if (!this.toggleBtn || !this.overlay) return;

      this.input = this.overlay.querySelector('.br-search-overlay__input');

      this.toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });

      /* Close on click outside the inner container */
      this.overlay.addEventListener('click', (e) => {
        if (!e.target.closest('.br-search-overlay__container')) this.close(false);
      });

      /* Close on Escape */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
      });
    }

    open() {
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (this.input) {
        setTimeout(() => this.input.focus(), 200);
      }
    }

    /* returnFocus solo con teclado (Esc); con ratón se suelta el foco para que
       la lupa no se quede con el anillo de :focus-visible. */
    close(returnFocus = true) {
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (returnFocus && this.toggleBtn) {
        this.toggleBtn.focus();
      } else if (this.overlay.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }
  }

  customElements.define('header-search', HeaderSearch);
}

/* ── <cart-counter> ── */
if (!customElements.get('cart-counter')) {
  class CartCounter extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-cart-count]');
    }

    update(count) {
      if (!this.countEl) {
        if (count > 0) {
          const span = document.createElement('span');
          span.className = 'br-header__cart-count';
          span.dataset.cartCount = '';
          span.textContent = count;
          const textWrap = this.querySelector('.br-header__cart-text');
          if (textWrap) textWrap.appendChild(span);
          this.countEl = span;
        }
        return;
      }

      if (count > 0) {
        this.countEl.textContent = count;
      } else {
        this.countEl.remove();
        this.countEl = null;
      }
    }
  }

  customElements.define('cart-counter', CartCounter);
}
